import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import ExchangeRates from '../components/ExchangeRates';

const Dashboard = () => {
  const { t } = useTranslation();
  const [overview, setOverview] = useState(null);
  const [taxStats, setTaxStats] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState('monthly'); // monthly, quarterly, yearly
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [overviewRes, taxStatsRes] = await Promise.all([
        dashboardAPI.getOverview(),
        dashboardAPI.getTaxStatistics({ year: selectedYear })
      ]);
      setOverview(overviewRes.data.overview);
      setTaxStats(taxStatsRes.data);
      setError('');
    } catch (err) {
      setError(t('dashboard.failedToLoad'));
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const currency = taxStats?.currency || 'PLN';
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const getMonthName = (monthNumber) => {
    const monthKeys = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];
    return t(`dashboard.${monthKeys[monthNumber - 1]}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pastel-purple-500"></div>
          <p className="text-lg text-gray-700 dark:text-gray-300">{t('dashboard.loadingDashboard')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-6 py-4 rounded-xl">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8 bg-gradient-to-r from-pastel-purple-600 to-pastel-blue-600 bg-clip-text text-transparent">
        {t('dashboard.financialDashboard')}
      </h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Total Income */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl p-6 border border-pastel-green-200 dark:border-pastel-green-800 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{t('dashboard.totalIncome')}</p>
              <p className="text-3xl font-bold text-pastel-green-600 dark:text-pastel-green-400">
                {formatCurrency(overview?.totalIncoming)}
              </p>
            </div>
            <div className="bg-pastel-green-100 dark:bg-pastel-green-900/30 p-3 rounded-xl">
              <svg className="w-8 h-8 text-pastel-green-600 dark:text-pastel-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Total Outgoing */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl p-6 border border-pastel-blue-200 dark:border-pastel-blue-800 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{t('dashboard.totalOutgoing')}</p>
              <p className="text-3xl font-bold text-pastel-blue-600 dark:text-pastel-blue-400">
                {formatCurrency(overview?.totalOutgoing)}
              </p>
            </div>
            <div className="bg-pastel-blue-100 dark:bg-pastel-blue-900/30 p-3 rounded-xl">
              <svg className="w-8 h-8 text-pastel-blue-600 dark:text-pastel-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Net Revenue */}
        <div className={`bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl p-6 border ${
          overview?.netRevenue >= 0
            ? 'border-pastel-purple-200 dark:border-pastel-purple-800'
            : 'border-red-200 dark:border-red-800'
        } hover:shadow-2xl transition-all duration-300 hover:-translate-y-1`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{t('dashboard.netRevenue')}</p>
              <p className={`text-3xl font-bold ${
                overview?.netRevenue >= 0
                  ? 'text-pastel-purple-600 dark:text-pastel-purple-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {formatCurrency(overview?.netRevenue)}
              </p>
            </div>
            <div className={`${
              overview?.netRevenue >= 0
                ? 'bg-pastel-purple-100 dark:bg-pastel-purple-900/30'
                : 'bg-red-100 dark:bg-red-900/30'
            } p-3 rounded-xl`}>
              <svg className={`w-8 h-8 ${
                overview?.netRevenue >= 0
                  ? 'text-pastel-purple-600 dark:text-pastel-purple-400'
                  : 'text-red-600 dark:text-red-400'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Exchange Rates Section */}
      <div className="mb-8">
        <ExchangeRates />
      </div>

      {/* Tax Statistics Section */}
      {taxStats && (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-white">{t('dashboard.taxStatistics')}</h2>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white font-medium focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              {[...Array(5)].map((_, i) => {
                const year = new Date().getFullYear() - i;
                return <option key={year} value={year} className="text-gray-900">{year}</option>;
              })}
            </select>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('monthly')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'monthly'
                    ? 'border-pastel-purple-500 text-pastel-purple-600 dark:text-pastel-purple-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {t('dashboard.monthly')}
              </button>
              <button
                onClick={() => setActiveTab('quarterly')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'quarterly'
                    ? 'border-pastel-purple-500 text-pastel-purple-600 dark:text-pastel-purple-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {t('dashboard.quarterly')}
              </button>
              <button
                onClick={() => setActiveTab('yearly')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'yearly'
                    ? 'border-pastel-purple-500 text-pastel-purple-600 dark:text-pastel-purple-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {t('dashboard.yearly')}
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'monthly' && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr className="bg-pastel-purple-50 dark:bg-gray-900/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.month')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.income')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.expenses')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.profit')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.vatCollected')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.vatPaid')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.vatBalance')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.incomeTax')} ({taxStats.incomeTaxRate}%)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {taxStats.monthly.map((month) => (
                      <tr key={month.month} className="hover:bg-pastel-purple-50 dark:hover:bg-gray-900/30 transition-colors">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{getMonthName(month.month)}</td>
                        <td className="px-4 py-3 text-sm text-right text-green-600 dark:text-green-400 font-semibold">{formatCurrency(month.income)}</td>
                        <td className="px-4 py-3 text-sm text-right text-red-600 dark:text-red-400 font-semibold">{formatCurrency(month.expenses)}</td>
                        <td className={`px-4 py-3 text-sm text-right font-bold ${month.profit >= 0 ? 'text-purple-600 dark:text-purple-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatCurrency(month.profit)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{formatCurrency(month.vatCollected)}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{formatCurrency(month.vatPaid)}</td>
                        <td className={`px-4 py-3 text-sm text-right font-semibold ${month.vatBalance >= 0 ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'}`}>
                          {formatCurrency(month.vatBalance)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300 font-semibold">{formatCurrency(month.incomeTax)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'quarterly' && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr className="bg-pastel-purple-50 dark:bg-gray-900/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.quarter')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.income')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.expenses')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.profit')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.vatCollected')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.vatPaid')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.vatBalance')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">{t('dashboard.incomeTax')} ({taxStats.incomeTaxRate}%)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {taxStats.quarterly.map((quarter) => (
                      <tr key={quarter.quarter} className="hover:bg-pastel-purple-50 dark:hover:bg-gray-900/30 transition-colors">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{quarter.quarterName}</td>
                        <td className="px-4 py-3 text-sm text-right text-green-600 dark:text-green-400 font-semibold">{formatCurrency(quarter.income)}</td>
                        <td className="px-4 py-3 text-sm text-right text-red-600 dark:text-red-400 font-semibold">{formatCurrency(quarter.expenses)}</td>
                        <td className={`px-4 py-3 text-sm text-right font-bold ${quarter.profit >= 0 ? 'text-purple-600 dark:text-purple-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatCurrency(quarter.profit)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{formatCurrency(quarter.vatCollected)}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{formatCurrency(quarter.vatPaid)}</td>
                        <td className={`px-4 py-3 text-sm text-right font-semibold ${quarter.vatBalance >= 0 ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'}`}>
                          {formatCurrency(quarter.vatBalance)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300 font-semibold">{formatCurrency(quarter.incomeTax)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'yearly' && taxStats.yearly && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('dashboard.totalIncome')}</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{formatCurrency(taxStats.yearly.income)}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('dashboard.totalExpenses')}</p>
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">{formatCurrency(taxStats.yearly.expenses)}</p>
                  </div>
                  <div className={`p-4 rounded-lg border ${taxStats.yearly.profit >= 0 ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('dashboard.totalProfit')}</p>
                    <p className={`text-2xl font-bold ${taxStats.yearly.profit >= 0 ? 'text-purple-600 dark:text-purple-400' : 'text-red-600 dark:text-red-400'}`}>
                      {formatCurrency(taxStats.yearly.profit)}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('dashboard.vatCollected')}</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(taxStats.yearly.vatCollected)}</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('dashboard.vatPaid')}</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(taxStats.yearly.vatPaid)}</p>
                  </div>
                  <div className={`p-4 rounded-lg border ${taxStats.yearly.vatBalance >= 0 ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'}`}>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('dashboard.vatToPay')}</p>
                    <p className={`text-2xl font-bold ${taxStats.yearly.vatBalance >= 0 ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'}`}>
                      {formatCurrency(taxStats.yearly.vatBalance)}
                    </p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('dashboard.incomeTax')} ({taxStats.incomeTaxRate}%)</p>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{formatCurrency(taxStats.yearly.incomeTax)}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('dashboard.totalTaxLiability')}</p>
                    <p className="text-3xl font-bold text-red-600 dark:text-red-400">{formatCurrency(taxStats.yearly.totalTaxLiability)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
